import React, { useState } from 'react';
import { StarIcon, FileTextIcon, BrainIcon, CalendarIcon } from 'lucide-react';
const PlayerHero = () => {
  const [dataTimeframe, setDataTimeframe] = useState('all_time');
  const [isFavorite, setIsFavorite] = useState(false);
  const [heroTab, setHeroTab] = useState<'sw' | 'neuro' | 'quick_stats'>('sw');
  const [matchTab, setMatchTab] = useState<'last' | 'next'>('next');
  const playerData = {
    name: 'NOME COGNOME',
    team: 'SQUADRA',
    number: 'NUM',
    role: 'RUOLO',
    height: 'ALTEZZA',
    weight: 'PESO',
    age: 'ETÀ',
    nationality: 'NAZIONALITÀ',
    quickStats: {
      all_time: {
        pts: 65,
        reb: 70,
        ast: 45,
        stl: 50,
        blk: 55,
        fgp: 60,
        tpp: 65,
        ftp: 75,
        orb: 40,
        drb: 60
      },
      this_season: {
        pts: 70,
        reb: 65,
        ast: 50,
        stl: 45,
        blk: 50,
        fgp: 65,
        tpp: 60,
        ftp: 80,
        orb: 45,
        drb: 55
      }
    },
    lastMatch: {
      score: '83 - 95',
      datetime: 'Feb 28, 2024',
      stats: {
        pts: 11,
        rim: 1,
        ast: 3
      }
    },
    nextMatch: {
      daysUntil: 1,
      opponent: {
        name: 'Celtics',
        logo: 'https://cdn.nba.com/logos/nba/1610612738/global/L/logo.svg',
        rank: 2,
        ptsAllowed: 108.5,
        streak: 'W4'
      },
      date: 'Mar 02, 2024',
      venue: 'TD Garden'
    }
  };
  const strengths = [{
    label: 'Dominant rebounder',
    stat: '10.2 RPG career avg'
  }, {
    label: 'Clutch performer',
    stat: '+4.3 PTS in 4th quarter'
  }, {
    label: 'High basketball IQ',
    stat: '7.4 APG for position'
  }, {
    label: 'Durable & consistent',
    stat: '98% games played'
  }];
  const weaknesses = [{
    label: 'Free throw struggles',
    stat: '69.8% FT career'
  }, {
    label: 'Turnover prone under pressure',
    stat: '3.1 TO/G in playoffs'
  }, {
    label: 'Lateral quickness declining',
    stat: '-12% defensive speed'
  }, {
    label: 'Off-ball movement',
    stat: 'Bottom 25th percentile'
  }];
  const neuroData = {
    archetype: 'THE COMMANDER',
    description: 'A floor general who thrives under pressure. Exceptional at reading defensive schemes and making split-second decisions. Maintains composure in high-leverage moments and elevates teammates through vocal leadership and precise execution.',
    scores: {
      attention: 82,
      prediction: 91,
      control: 75,
      resilience: 88
    }
  };
  // Helper for countdown formatting
  const formatCountdown = (days: number) => {
    return <div className="flex items-baseline justify-center gap-1.5" data-id="element-281" data-mp="1eutxr4atchm2">
        <span className="text-gray-500 text-xl font-medium lowercase" data-id="element-282" data-mp="22lz9b79q0nqk">in</span>
        <div className="flex items-center" data-id="element-283" data-mp="22jwfec5g6agk">
          <span data-id="element-284" data-mp="27dhc80vb0ye6">35hrs</span>
          <span className="animate-pulse mx-0.5" data-id="element-285" data-mp="27dqut3e0s39u">:</span>
          <span data-id="element-286" data-mp="3s21mq0jc2bzm">49min</span>
        </div>
      </div>;
  };
  const isUrgent = playerData.nextMatch.daysUntil < 2;
  // Color helpers
  const rankColor = (rank: number) => rank <= 10 ? 'text-emerald-600' : rank >= 20 ? 'text-red-600' : 'text-amber-600';
  const ptsAllowedColor = (pts: number) => pts <= 110 ? 'text-amber-600' : pts >= 118 ? 'text-emerald-600' : 'text-amber-600';
  const streakColor = (streak: string) => {
    const type = streak[0];
    const n = parseInt(streak.slice(1)) || 0;
    if (type === 'W' && n >= 3) return 'text-red-600';
    if (type === 'L' && n >= 2) return 'text-emerald-600';
    return 'text-amber-600';
  };
  return <div className="grid grid-cols-4 gap-4" data-id="element-287" data-mp="3d4ix6vytzur4">
      <div className="col-span-1 bg-white p-4 border rounded-lg shadow-sm" data-id="element-288" data-mp="3d7x3gfam8o3u">
        <div className="flex flex-col justify-between h-full" data-id="element-289" data-mp="xclczkq5q96b">
          <div className="w-full h-32 bg-gray-100 rounded-lg overflow-hidden relative" data-id="element-290" data-mp="sg4ogpf1pssp">
            <img src="https://cdn.nba.com/headshots/nba/latest/1040x760/2544.png" alt="Player photo" className="w-full h-full object-cover object-top" data-id="element-291" data-mp="1nq1g75rt5fow" />
            <img src="https://flagicons.lipis.dev/flags/4x3/us.svg" alt="United States" className="w-5 h-4 absolute bottom-1 right-1 shadow-sm" data-id="element-292" data-mp="1xnfxf7lk28i9" />
            <div className="absolute bottom-1 left-1 flex flex-col items-center" data-id="element-293" data-mp="26p5noa3pb1eg">
              <img src="https://cdn.nba.com/logos/nba/1610612747/global/L/logo.svg" alt="LA Lakers" className="w-8 h-8 object-contain" data-id="element-294" data-mp="2bn756qlhasnt" />
              <span className="text-[10px] uppercase tracking-wider text-gray-600 bg-white/80 px-1 rounded font-bold" data-id="element-295" data-mp="2ld0gdqhpl5rj">
                Lakers
              </span>
            </div>
          </div>

          <div className="mt-3 space-y-1.5" data-id="element-296" data-mp="2udovkoiu9c60">
            <div className="font-bold text-gray-900 text-sm" data-id="element-297" data-mp="2uehux4fgwze4">
              #23 | POWER FORWARD
            </div>
            <div className="space-y-1" data-id="element-298" data-mp="irxtf74u0oza">
              <div className="flex items-center text-sm text-gray-600" data-id="element-299" data-mp="eoi1up7mdetg">
                <span className="w-20" data-id="element-300" data-mp="erdyma7qdxgb">Height:</span>
                <span className="font-medium" data-id="element-301" data-mp="9qztazxil1lz">6'9"</span>
              </div>
              <div className="flex items-center text-sm text-gray-600" data-id="element-302" data-mp="4rdbseankz7d">
                <span className="w-20" data-id="element-303" data-mp="4t6i1shctheu">Weight:</span>
                <span className="font-medium" data-id="element-304" data-mp="12daqe0lwwke1">250 lbs</span>
              </div>
              <div className="flex items-center text-sm text-gray-600" data-id="element-305" data-mp="xf95rb8u8d0g">
                <span className="w-20" data-id="element-306" data-mp="sossps8xitem">Birthday:</span>
                <span className="font-medium" data-id="element-307" data-mp="sn7xs1ipd8g8">30.07.1983</span>
              </div>
              <div className="flex items-center text-sm text-gray-600" data-id="element-308" data-mp="noods6no66qi">
                <span className="w-20" data-id="element-309" data-mp="1l5lntexsrq4r">League:</span>
                <span className="font-medium" data-id="element-310" data-mp="1l6enjvyc9fde">NBA</span>
              </div>
            </div>

            <div className="flex justify-center space-x-2 pt-2 mt-2 border-t" data-id="element-311" data-mp="1gcsfyi7181og">
              <button className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200" data-id="element-312" data-mp="3ana99umk616p">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-313" data-mp="3eo2hqlnlw21x">
                  <path d="M12 19c-2.3 0-6.4-.2-8.1-.6-.7-.2-1.2-.7-1.4-1.4-.3-1.1-.5-3.4-.5-5s.2-3.9.5-5c.2-.7.7-1.2 1.4-1.4C5.6 5.2 9.7 5 12 5s6.4.2 8.1.6c.7.2 1.2.7 1.4 1.4.3 1.1.5 3.4.5 5s-.2 3.9-.5 5c-.2.7-.7 1.2-1.4 1.4-1.7.4-5.8.6-8.1.6 0 0 0 0 0 0z" data-id="element-314" data-mp="2vy47q008zyvn"></path>
                  <polygon points="10 15 15 12 10 9" data-id="element-315" data-mp="g5nfq0vn15aj"></polygon>
                </svg>
              </button>
              <button className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200" data-id="element-316" data-mp="l5t8jkdn8xu7">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-317" data-mp="v8yleg5i5b7y">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" data-id="element-318" data-mp="1dywsasxkcya0"></path>
                </svg>
              </button>
              <button className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200" data-id="element-319" data-mp="190d2uijrh4cw">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-320" data-mp="1mzuowq5oa3ik">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" data-id="element-321" data-mp="d3lthc5cihh7"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" data-id="element-322" data-mp="i388nr4qi7sn"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" data-id="element-323" data-mp="mwswwrgiznen"></line>
                </svg>
              </button>
              <button className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200" data-id="element-324" data-mp="qwlajiks0jqv">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-325" data-mp="3qdjjoi0buf31">
                  <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" data-id="element-326" data-mp="1emfknsdpnu7f"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="col-span-2 bg-white p-5 border rounded-lg shadow-sm flex flex-col h-full" data-id="element-327" data-mp="1orniuuvs1cjk">
        {/* Tabs */}
        <div className="flex items-center border-b mb-0" data-id="element-328" data-mp="1jycldkcuu84b">
          <button onClick={() => setHeroTab('sw')} className={`px-3 py-2 text-xs font-bold tracking-wider border-b-2 transition-colors ${heroTab === 'sw' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-600'}`} data-id="element-329" data-mp="1fyu0upoy3z20">
            S&W
          </button>
          <button onClick={() => setHeroTab('neuro')} className={`px-3 py-2 text-xs font-bold tracking-wider border-b-2 transition-colors flex items-center gap-1.5 ${heroTab === 'neuro' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-600'}`} data-id="element-330" data-mp="11230ad3jchat">
            <BrainIcon className="w-3.5 h-3.5" data-id="element-331" data-mp="2rdz5w0cju9os" />
            NEURO
          </button>
          <button onClick={() => setHeroTab('quick_stats')} className={`px-3 py-2 text-xs font-bold tracking-wider border-b-2 transition-colors ${heroTab === 'quick_stats' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-600'}`} data-id="element-332" data-mp="19uzdqb0fkil9">
            QUICK STATS
          </button>
        </div>

        {/* Tab Content */}
        <div className="flex-1 flex flex-col pt-4 overflow-hidden" data-id="element-333" data-mp="r4rei3zuqcac">
          {/* S&W Tab */}
          {heroTab === 'sw' && <div className="flex gap-5 flex-1" data-id="element-334" data-mp="m80zzl3p3or1">
              {/* Strengths */}
              <div className="flex-1" data-id="element-335" data-mp="10wdu0cj82xiw">
                <div className="flex items-center gap-1.5 mb-3" data-id="element-336" data-mp="10xf5gi45hm15">
                  <div className="w-2 h-2 rounded-full bg-emerald-600" data-id="element-337" data-mp="w3u88sadsw30"></div>
                  <h4 className="text-[10px] uppercase tracking-wider font-bold text-emerald-600" data-id="element-338" data-mp="8e9o462hrss5">
                    Strengths
                  </h4>
                </div>
                <ul className="space-y-2.5" data-id="element-339" data-mp="2f3aal18lvmdz">
                  {strengths.map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-340" data-mp="2ju0fkdd4dn85" data-mpi={i}>
                      <span className="text-emerald-600 mt-0.5 text-xs" data-id="element-341" data-mp="2jvbiyc2nksyq">●</span>
                      <div data-id="element-342" data-mp="263ww1u5mk7me">
                        <span className="text-xs font-bold text-gray-900" data-id="element-343" data-mp="260iwetocicin">
                          {item.label}
                        </span>
                        <span className="text-xs text-emerald-600 font-bold ml-1.5" data-id="element-344" data-mp="2xtjlczigkmha">
                          {item.stat}
                        </span>
                      </div>
                    </li>)}
                </ul>
              </div>

              {/* Divider */}
              <div className="w-px bg-gray-200" data-id="element-345" data-mp="2troj9sspe52g"></div>

              {/* Weaknesses */}
              <div className="flex-1" data-id="element-346" data-mp="1de7unfp065k9">
                <div className="flex items-center gap-1.5 mb-3" data-id="element-347" data-mp="1dd57niuswn9m">
                  <div className="w-2 h-2 rounded-full bg-red-600" data-id="element-348" data-mp="1idkio3rm1iof"></div>
                  <h4 className="text-[10px] uppercase tracking-wider font-bold text-red-600" data-id="element-349" data-mp="1iaolwiri101k">
                    Weaknesses
                  </h4>
                </div>
                <ul className="space-y-2.5" data-id="element-350" data-mp="3f3nfttsh5eq7">
                  {weaknesses.map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-351" data-mp="3aaue7esaymic" data-mpi={i}>
                      <span className="text-red-600 mt-0.5 text-xs" data-id="element-352" data-mp="36bljo7pfuklq">●</span>
                      <div data-id="element-353" data-mp="31bfq2lzlyo10">
                        <span className="text-xs font-bold text-gray-900" data-id="element-354" data-mp="31cqqcbtuhdnd">
                          {item.label}
                        </span>
                        <span className="text-xs text-red-600 font-bold ml-1.5" data-id="element-355" data-mp="2wez15pkl45mv">
                          {item.stat}
                        </span>
                      </div>
                    </li>)}
                </ul>
              </div>
            </div>}

          {/* Neuro Tab */}
          {heroTab === 'neuro' && <div className="flex gap-5 flex-1" data-id="element-356" data-mp="llmgebjc8zpg">
              {/* Archetype Text */}
              <div className="flex-1 flex flex-col" data-id="element-357" data-mp="23izl0x4gtokq">
                <h4 className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1" data-id="element-358" data-mp="23g3j6xphyfr0">
                  Archetype
                </h4>
                <div className="text-lg font-bold text-gray-900 mb-2" data-id="element-359" data-mp="2d7r7wquo5ios">
                  {neuroData.archetype}
                </div>
                <p className="text-xs text-gray-600 leading-relaxed" data-id="element-360" data-mp="2h8t9hec266tx">
                  {neuroData.description}
                </p>
              </div>

              {/* Radar Chart */}
              <div className="flex-1 flex flex-col justify-center divide-y divide-gray-100" data-id="element-361" data-mp="1pj82cj307k0e">
                {([{
              label: 'Control',
              value: neuroData.scores.control
            }, {
              label: 'Attention',
              value: neuroData.scores.attention
            }, {
              label: 'Prediction',
              value: neuroData.scores.prediction
            }, {
              label: 'Resilience',
              value: neuroData.scores.resilience
            }] as const).map(item => <div key={item.label} className="flex items-center justify-between py-2.5 px-1" data-id="element-362" data-mp="3dfxyejvnh850">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-363" data-mp="3rdm7yvk9osur">
                      {item.label}
                    </span>
                    <span className={`text-sm font-bold ${item.value >= 80 ? 'text-emerald-600' : item.value >= 60 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-364" data-mp="4z2wp7ql9tsi">
                      {item.value}
                    </span>
                  </div>)}
              </div>
            </div>}

          {/* Quick Stats Tab */}
          {heroTab === 'quick_stats' && <>
              <div className="flex justify-end mb-3" data-id="element-365" data-mp="xmugbgs9z776">
                <div className="flex bg-gray-100 rounded-md p-0.5" data-id="element-366" data-mp="xljg1qy1ghkt">
                  <button onClick={() => setDataTimeframe('all_time')} className={`px-3 py-1 text-xs font-medium rounded ${dataTimeframe === 'all_time' ? 'bg-gray-900 text-white' : 'text-gray-600'}`} data-id="element-367" data-mp="jnmfkjggrr0v">
                    all time
                  </button>
                  <button onClick={() => setDataTimeframe('this_season')} className={`px-3 py-1 text-xs font-medium rounded ${dataTimeframe === 'this_season' ? 'bg-gray-900 text-white' : 'text-gray-600'}`} data-id="element-368" data-mp="ep1nx8fa2fo1">
                    this season
                  </button>
                </div>
              </div>

              <div className="flex flex-col flex-1 justify-evenly" data-id="element-369" data-mp="3s2mxiklzpjav">
                <div className="grid grid-cols-5 gap-4" data-id="element-370" data-mp="3s4zbynsjhbj6">
                  {Object.entries(playerData.quickStats[dataTimeframe as 'all_time' | 'this_season']).slice(0, 5).map(([key, value]) => <div key={key} className="flex flex-col items-center" data-id="element-371" data-mp="1jv9kmlsl69yb">
                        <div className="w-14 h-14 rounded-full border-4 border-gray-200 flex items-center justify-center relative" data-id="element-372" data-mp="1jt6qbpkehuns">
                          <div className="absolute inset-0 rounded-full border-4 border-blue-600" style={{
                    clipPath: `polygon(0 0, 100% 0, 100% ${value}%, 0 ${value}%)`,
                    opacity: 0.7
                  }} data-id="element-373" data-mp="2blxnq3dtlpem"></div>
                          <span className="text-sm font-bold text-gray-900" data-id="element-374" data-mp="w3m3gvl4md98">
                            {value}%
                          </span>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mt-2" data-id="element-375" data-mp="32vipfjiql3lm">
                          {key}
                        </span>
                      </div>)}
                </div>
                <div className="grid grid-cols-5 gap-4" data-id="element-376" data-mp="3glcdhzmf3jua">
                  {Object.entries(playerData.quickStats[dataTimeframe as 'all_time' | 'this_season']).slice(5, 10).map(([key, value]) => <div key={key} className="flex flex-col items-center" data-id="element-377" data-mp="3vid7k9s2zqvx">
                        <div className="w-14 h-14 rounded-full border-4 border-gray-200 flex items-center justify-center relative" data-id="element-378" data-mp="3ekecejmky6t">
                          <div className="absolute inset-0 rounded-full border-4 border-blue-600" style={{
                    clipPath: `polygon(0 0, 100% 0, 100% ${value}%, 0 ${value}%)`,
                    opacity: 0.7
                  }} data-id="element-379" data-mp="215nbble8q5ld"></div>
                          <span className="text-sm font-bold text-gray-900" data-id="element-380" data-mp="1v20lps1oefdm">
                            {value}%
                          </span>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mt-2" data-id="element-381" data-mp="1r2rp8ff943ed">
                          {key}
                        </span>
                      </div>)}
                </div>
              </div>
            </>}
        </div>
      </div>

      <div className="col-span-1 bg-white border rounded-lg shadow-sm flex flex-col" data-id="element-382" data-mp="1c9ekes801wln">
        {/* Tabs */}
        <div className="flex items-center border-b" data-id="element-383" data-mp="2nqxtv2c6avsn">
          <button onClick={() => setMatchTab('last')} className={`flex-1 px-3 py-2 text-xs font-bold tracking-wider border-b-2 transition-colors ${matchTab === 'last' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-600'}`} data-id="element-384" data-mp="2npcumzewyoog">
            LAST MATCH
          </button>
          <button onClick={() => setMatchTab('next')} className={`flex-1 px-3 py-2 text-xs font-bold tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${matchTab === 'next' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-600'}`} data-id="element-385" data-mp="2hantxzg4td3t">
            <img src="https://cdn.magicpatterns.com/uploads/7ci16aCeWUqLeiaLpspp2J/hotnow_incoming.svg" alt="Incoming Match" className={`w-7 h-10 transition-all duration-300 ${matchTab === 'next' ? 'opacity-100' : 'opacity-50'}`} data-id="element-386" data-mp="3exeeikiizjrk" />
            <span data-id="element-387" data-mp="3sxxldob98js5">NEXT MATCH</span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-4 flex-1" data-id="element-388" data-mp="1k1o1hyj4hod">
          {matchTab === 'last' && <div className="text-center" data-id="element-389" data-mp="1t4m9cy5eaon3">
              <div className="text-3xl font-bold text-center mb-3 py-3 text-gray-900" data-id="element-390" data-mp="1t5p0n76mqt3j">
                {playerData.lastMatch.score}
              </div>
              <div className="flex justify-center items-center space-x-8" data-id="element-391" data-mp="1jeucfx9tpjfg">
                <div className="flex flex-col items-center" data-id="element-392" data-mp="1jekmuasoxeaa">
                  <img src="https://cdn.nba.com/logos/nba/1610612747/global/L/logo.svg" alt="LA Lakers" className="w-12 h-12 object-contain" data-id="element-393" data-mp="1ocvuqfzfdepv" />
                  <span className="text-sm font-medium text-gray-600 mt-1" data-id="element-394" data-mp="1ehb052worgq5">
                    Lakers
                  </span>
                </div>
                <div className="flex flex-col items-center" data-id="element-395" data-mp="15erxaiswh43w">
                  <img src="https://cdn.nba.com/logos/nba/1610612752/global/L/logo.svg" alt="New York Knicks" className="w-12 h-12 object-contain" data-id="element-396" data-mp="15fbg01qdjwiz" />
                  <span className="text-sm font-medium text-gray-600 mt-1" data-id="element-397" data-mp="39x0x5zm8xsty">
                    Knicks
                  </span>
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1" data-id="element-398" data-mp="34zrc0w5w43bx">
                <div data-id="element-399" data-mp="3ipkzba1qyfjj">Feb 28, 2024</div>
                <div className="text-[10px] uppercase tracking-wider mt-1" data-id="element-400" data-mp="3iqmb5gql762b">
                  Madison Square Garden
                </div>
              </div>
              <div className="flex justify-between items-center mt-4" data-id="element-401" data-mp="3smpozjuswt53">
                <div className="text-center text-2xl" data-id="element-402" data-mp="3noed73ky48k8">
                  <div className="font-bold text-gray-900" data-id="element-403" data-mp="3nnw9xmzv8u2t">
                    {playerData.lastMatch.stats.pts}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500" data-id="element-404" data-mp="5ggilcx3zljj">
                    PTS
                  </div>
                </div>
                <div className="text-center text-2xl px-3" data-id="element-405" data-mp="27axoyrl5qb2k">
                  <div className="font-bold text-gray-900" data-id="element-406" data-mp="238udw42eh8ht">
                    {playerData.lastMatch.stats.rim}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500" data-id="element-407" data-mp="6986o34oqngr">
                    RIM
                  </div>
                </div>
                <div className="text-center text-2xl" data-id="element-408" data-mp="g6dzpgwv0ojm">
                  <div className="font-bold text-gray-900" data-id="element-409" data-mp="g4t4dp2q11kp">
                    {playerData.lastMatch.stats.ast}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500" data-id="element-410" data-mp="p6spotkrqrtg">
                    AST
                  </div>
                </div>
              </div>
            </div>}

          {matchTab === 'next' && <div className="text-center" data-id="element-411" data-mp="yxx0ri7bpsj0">
              {/* Countdown — same visual hierarchy as the score */}
              <div className="text-3xl font-bold text-center mb-3 py-3 tracking-tight text-gray-900" data-id="element-412" data-mp="13v5mttvlegpj">
                {formatCountdown(playerData.nextMatch.daysUntil)}
              </div>

              {/* Team logos */}
              <div className="flex justify-center items-center space-x-8" data-id="element-413" data-mp="1cuakyz38s816">
                <div className="flex flex-col items-center" data-id="element-414" data-mp="2xfz3dr23tr63">
                  <img src="https://cdn.nba.com/logos/nba/1610612747/global/L/logo.svg" alt="LA Lakers" className="w-12 h-12 object-contain" data-id="element-415" data-mp="2tcaw79hdwxld" />
                  <span className="text-sm font-medium text-gray-600 mt-1" data-id="element-416" data-mp="2jfoa6hdbo0hr">
                    Lakers
                  </span>
                </div>
                <div className="flex flex-col items-center" data-id="element-417" data-mp="3h47zgc6rqhav">
                  <img src={playerData.nextMatch.opponent.logo} alt={playerData.nextMatch.opponent.name} className="w-12 h-12 object-contain" data-id="element-418" data-mp="3c3tz7gbesbne" />
                  <span className="text-sm font-medium text-gray-600 mt-1" data-id="element-419" data-mp="32befadgjxczv">
                    {playerData.nextMatch.opponent.name}
                  </span>
                </div>
              </div>

              {/* Date + venue */}
              <div className="text-xs text-gray-500 mt-1" data-id="element-420" data-mp="3fjn3ui0z9nic">
                <div data-id="element-421" data-mp="3fm9b0gjyi101">{playerData.nextMatch.date}</div>
                <div className="text-[10px] uppercase tracking-wider mt-1" data-id="element-422" data-mp="3jm06gxkfh5aw">
                  {playerData.nextMatch.venue}
                </div>
              </div>

              {/* Opponent context row */}
              <div className="flex justify-between items-center mt-4" data-id="element-423" data-mp="3am4q8ec0mk40">
                <div className="text-center text-2xl" data-id="element-424" data-mp="25i9zih57jeb">
                  <div className={`font-bold ${rankColor(playerData.nextMatch.opponent.rank)}`} data-id="element-425" data-mp="261u95u9mjvi">
                    #{playerData.nextMatch.opponent.rank}
                  </div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-426" data-mp="3oj0dpbt0ke3l">
                    Opp Rank
                  </div>
                </div>
                <div className="text-center text-2xl px-2" data-id="element-427" data-mp="l3ckk0xx62yt">
                  <div className={`font-bold ${ptsAllowedColor(playerData.nextMatch.opponent.ptsAllowed)}`} data-id="element-428" data-mp="pukv50azjudk">
                    {playerData.nextMatch.opponent.ptsAllowed}
                  </div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-429" data-mp="25ldn2wk9ey7h">
                    Pts Allowed
                  </div>
                </div>
                <div className="text-center text-2xl" data-id="element-430" data-mp="2fc851oqh3bn4">
                  <div className={`font-bold ${streakColor(playerData.nextMatch.opponent.streak)}`} data-id="element-431" data-mp="2fcrpbc3lic4b">
                    {playerData.nextMatch.opponent.streak}
                  </div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-432" data-mp="20n2bohebgfn5">
                    Streak
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
};
export default PlayerHero;